
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import certificateService from "../services/certificateService";

const styles = {
    page: {
        minHeight: "100%",
        padding: 24,
        background: "#F7F5FC",
        color: "#29243A",
    },
    header: {
        background:
            "linear-gradient(120deg, #4C1D95 0%, #7C3AED 55%, #A855F7 100%)",
        borderRadius: 16,
        padding: "24px 28px",
        color: "#FFFFFF",
        marginBottom: 22,
        boxShadow: "0 8px 24px rgba(91, 33, 182, 0.16)",
    },
    card: {
        background: "#FFFFFF",
        border: "1px solid #E9E3F5",
        borderRadius: 14,
        padding: 24,
        marginBottom: 20,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: 700,
        margin: "0 0 20px",
        paddingBottom: 12,
        borderBottom: "1px solid #EEE8F7",
        color: "#4C1D95",
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 22,
    },
    label: {
        fontSize: 12,
        color: "#7C6C91",
        marginBottom: 6,
    },
    value: {
        fontSize: 14,
        fontWeight: 600,
        overflowWrap: "anywhere",
    },
    button: {
        border: 0,
        borderRadius: 9,
        padding: "11px 20px",
        color: "#FFFFFF",
        fontWeight: 700,
        fontSize: 13,
        cursor: "pointer",
        background: "linear-gradient(110deg, #6D28D9, #A855F7)",
    },
    secondaryButton: {
        border: "1px solid #D9CDEA",
        borderRadius: 9,
        padding: "11px 20px",
        color: "#5B21B6",
        fontWeight: 600,
        fontSize: 13,
        cursor: "pointer",
        background: "#FFFFFF",
    },
};

function Detail({ label, value }) {
    return (
        <div>
            <div style={styles.label}>{label}</div>
            <div style={styles.value}>
                {value === null || value === undefined || value === ""
                    ? "-"
                    : String(value)}
            </div>
        </div>
    );
}

function formatDate(value) {
    if (!value) return "-";

    const date = new Date(`${value}`.slice(0, 10) + "T00:00:00");

    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

function formatFileSize(bytes) {
    if (bytes === null || bytes === undefined || bytes === "") {
        return null;
    }

    const size = Number(bytes);

    if (!Number.isFinite(size) || size < 0) {
        return null;
    }

    if (size < 1024) {
        return `${size} bytes`;
    }

    return `${(size / 1024).toFixed(1)} KB`;
}

export default function CertificateViewPage() {
    const navigate = useNavigate();
    const { certificateId } = useParams();

    const [certificate, setCertificate] = useState(null);
    const [certificateDocument, setCertificateDocument] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState("");

    // Load certificate details.
    useEffect(() => {
        let mounted = true;

        async function loadCertificate() {
            setLoading(true);
            setError("");

            try {
                const result = await certificateService.getCertificate(
                    certificateId
                );

                if (mounted) {
                    setCertificate(result);
                }
            } catch (err) {
                if (mounted) {
                    setError(
                        err?.response?.data?.message ||
                            "Unable to load certificate details."
                    );
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        }

        loadCertificate();

        return () => {
            mounted = false;
        };
    }, [certificateId]);

    // Load the current uploaded document metadata.
    useEffect(() => {
        let mounted = true;

        async function loadCertificateDocument() {
            try {
                const document =
                    await certificateService.getCertificateDocument(
                        certificateId
                    );

                if (mounted) {
                    setCertificateDocument(document || null);
                }
            } catch (err) {
                if (mounted) {
                    setCertificateDocument(null);
                }
            }
        }

        loadCertificateDocument();

        return () => {
            mounted = false;
        };
    }, [certificateId]);

    // Download the uploaded certificate document.
    async function handleDownload() {
        if (!certificateDocument || downloading) {
            return;
        }

        setDownloading(true);
        setDownloadError("");

        try {
            await certificateService.downloadCertificateDocument(
                certificateId
            );
        } catch (err) {
            setDownloadError(
                err?.response?.data?.message ||
                    "Unable to download the certificate document."
            );
        } finally {
            setDownloading(false);
        }
    }

    if (loading) {
        return (
            <div style={styles.page}>
                Loading certificate details...
            </div>
        );
    }

    if (error || !certificate) {
        return (
            <div style={styles.page}>
                <div
                    role="alert"
                    style={{
                        padding: 15,
                        border: "1px solid #FECACA",
                        borderRadius: 9,
                        background: "#FEF2F2",
                        color: "#991B1B",
                        marginBottom: 16,
                    }}
                >
                    {error || "Certificate not found."}
                </div>

                <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={() =>
                        navigate("/assets/certificate-management")
                    }
                >
                    Back to Register
                </button>
            </div>
        );
    }

    const typeName =
    certificate.certificate_type?.type_name ||
    certificate.certificate_type?.name ||
    certificate.certificate_type_name ||
    certificate.type_name ||
    "-";

    const fileSize = formatFileSize(
        certificateDocument?.file_size_bytes
    );

    return (
        <div style={styles.page}>
            {/* Header */}
            <div style={styles.header}>
                <div style={{ fontSize: 24, fontWeight: 750 }}>
                    Certificate Details
                </div>

                <div
                    style={{
                        fontSize: 13,
                        marginTop: 7,
                        opacity: 0.9,
                    }}
                >
                    Assets / Certificate Management / View
                </div>

                <div style={{ fontSize: 13, marginTop: 15 }}>
                    Certificate Code:{" "}
                    <strong>
                        {certificate.certificate_code || "-"}
                    </strong>
                </div>
            </div>

            {/* 01. Certificate Information */}
            <section style={styles.card}>
                <h2 style={styles.sectionTitle}>
                    01. Certificate Information
                </h2>

                <div style={styles.grid}>
                    <Detail
                        label="Certificate Code"
                        value={certificate.certificate_code}
                    />

                    <Detail
                        label="Certificate Name"
                        value={certificate.certificate_name}
                    />

                    <Detail
                        label="Certificate Type"
                        value={typeName}
                    />

                    <Detail
                        label="Certificate Number"
                        value={certificate.certificate_number}
                    />

                    <Detail
                        label="Issuing Authority"
                        value={certificate.issuing_authority}
                    />

                    <Detail
                        label="Issued To"
                        value={certificate.issued_to}
                    />

                    <Detail
                        label="Branch / Location"
                        value={certificate.branch}
                    />

                    <Detail
                        label="Asset Reference ID"
                        value={certificate.asset_reference_id}
                    />
                </div>
            </section>

            {/* 02. Validity Details */}
            <section style={styles.card}>
                <h2 style={styles.sectionTitle}>
                    02. Validity Details
                </h2>

                <div style={styles.grid}>
                    <Detail
                        label="Issue Date"
                        value={formatDate(certificate.issue_date)}
                    />

                    <Detail
                        label="Expiry Date"
                        value={formatDate(certificate.expiry_date)}
                    />
                </div>
            </section>

            {/* 03. Additional Details */}
            <section style={styles.card}>
                <h2 style={styles.sectionTitle}>
                    03. Additional Details
                </h2>

                <Detail
                    label="Remarks"
                    value={certificate.remarks}
                />
            </section>

            {/* 04. Certificate Document */}
            <section style={styles.card}>
                <h2 style={styles.sectionTitle}>
                    04. Certificate Document
                </h2>

                <div
                    style={{
                        fontSize: 13,
                        color: "#7C6C91",
                        marginBottom: 15,
                    }}
                >
                    Download the uploaded certificate document.
                </div>

                {/* Uploaded filename and file size */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        flexWrap: "wrap",
                        padding: "12px 14px",
                        marginBottom: 16,
                        border: "1px solid #E9E3F5",
                        borderRadius: 9,
                        background: "#FAF8FF",
                    }}
                >
                    <span
                        style={{
                            fontSize: 22,
                            flexShrink: 0,
                        }}
                    >
                        📄
                    </span>

                    <div
                        style={{
                            minWidth: 0,
                            flex: 1,
                        }}
                    >
                        <div
                            style={{
                                fontSize: 11,
                                color: "#7C6C91",
                                marginBottom: 5,
                            }}
                        >
                            Uploaded File
                        </div>

                        <div
                            style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: "#4C1D95",
                                overflowWrap: "anywhere",
                            }}
                        >
                            {certificateDocument?.original_file_name ||
                                "No document uploaded"}
                        </div>

                        {fileSize && (
                            <div
                                style={{
                                    fontSize: 11,
                                    color: "#7C6C91",
                                    marginTop: 4,
                                }}
                            >
                                {fileSize}
                            </div>
                        )}
                    </div>
                </div>

                {/* Download error */}
                {downloadError && (
                    <div
                        role="alert"
                        style={{
                            padding: 12,
                            marginBottom: 15,
                            borderRadius: 8,
                            border: "1px solid #FECACA",
                            background: "#FEF2F2",
                            color: "#991B1B",
                            fontSize: 13,
                        }}
                    >
                        {downloadError}
                    </div>
                )}

                {/* Download button */}
                <button
                    type="button"
                    style={{
                        ...styles.button,
                        opacity:
                            downloading || !certificateDocument
                                ? 0.65
                                : 1,
                        cursor:
                            downloading || !certificateDocument
                                ? "not-allowed"
                                : "pointer",
                    }}
                    onClick={handleDownload}
                    disabled={downloading || !certificateDocument}
                >
                    {downloading
                        ? "Downloading..."
                        : "⬇ Download Certificate"}
                </button>
            </section>

            {/* Footer Actions */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 12,
                    flexWrap: "wrap",
                    paddingBottom: 24,
                }}
            >
                <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={() =>
                        navigate("/assets/certificate-management")
                    }
                >
                    Back to Register
                </button>

                <button
                    type="button"
                    style={styles.button}
                    onClick={() =>
                        navigate(
                            `/assets/certificate-management/${certificate.id}/edit`
                        )
                    }
                >
                    Edit Certificate
                </button>
            </div>
        </div>
    );
}
